import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { isEmpty, isNil } from "lodash-es";
import { useState } from "react";
import { Link as RouterLink } from "react-router-dom";

import PageTabContent from "@/App/Layouts/PageBody/PageTabContent";
import PageTabHeader from "@/App/Layouts/PageBody/PageTabHeader";
import ViewContentBlock from "@/App/Layouts/ViewContentBlock";
import { ViewLayout } from "@/App/Layouts/ViewLayout";
import AuthorizedElement from "@/common/components/Auth/AuthorizedElement";
import AuthorizedTenantElement from "@/common/components/Auth/AuthorizedTenantElement";
import AppIconButton from "@/common/components/Button/AppIconButton";
import DataTabular from "@/common/components/DataTabular/DataTabular";
import Datetime from "@/common/components/Datetime/Datetime";
import GeneralCurrencyDisplay from "@/common/components/Entity/General/Display/GeneralCurrencyDisplay";
import GeneralInspectorDisplay from "@/common/components/Entity/General/Display/GeneralInspectorDisplay";
import { RepairOperationAddVehicleDamagesModal } from "@/common/components/Entity/RepairOperation/RepairOperationAddVehicleDamagesModal";
import RepairSpecCreateUpdateModal from "@/common/components/Entity/RepairSpec/CreateUpdate/RepairSpecCreateUpdateModal";
import RepairSpecLink from "@/common/components/Entity/RepairSpec/RepairSpecLink";
import VehicleDamageLink from "@/common/components/Entity/VehicleDamage/VehicleDamageLink";
import EntityChangedByInfoCard from "@/common/components/EntityInfo/EntityChangedByInfoCard";
import InlineApiEnumValue from "@/common/components/Enum/InlineApiEnumValue";
import FileListView from "@/common/components/Files/FileListView";
import CurrencyValue from "@/common/components/Form/Display/CurrencyValue";
import InfoTile from "@/common/components/Form/Display/InfoTile";
import GeneralStageHistory from "@/common/components/GeneralStageHistory";
import AppIcon from "@/common/components/Icons/AppIcon";
import GeneralEntitySourceLink from "@/common/components/Link/GeneralEntitySourceLink";
import AppMenuItem from "@/common/components/Menu/AppMenuItem";
import MenuWithTrigger from "@/common/components/Menu/MenuWithTrigger";
import ConfirmationModal from "@/common/components/Modals/ConfirmationModal";
import BaseEditExternalNumberModal from "@/common/components/Modals/common/BaseEditExternalNumberModal";
import BaseEditNotesModal from "@/common/components/Modals/common/BaseEditNotesModal";
import AppPopover from "@/common/components/Popover/AppPopover";
import AppPopoverContent from "@/common/components/Popover/AppPopoverContent";
import GeneralPriceSummaryDisplay from "@/common/components/PriceSummary/GeneralPriceSummaryDisplay";
import AppTypography from "@/common/components/Text/AppTypography";
import AppTooltip from "@/common/components/Tooltip/AppTooltip";
import { ATTACHMENTS_COMMON_ITEM_SIZES } from "@/common/constants/common";
import { ROUTE_PATH } from "@/common/constants/routing";
import { FileItem } from "@/common/fileItem";
import { EntityHelper } from "@/common/helpers/entity";
import { DamageCostEvaluationHelper } from "@/common/helpers/entity/damageCostEvaluation";
import { renderIf } from "@/common/helpers/render/renderIf";
import { TypeHelper } from "@/common/helpers/type";
import { useAuthorizationInfo } from "@/common/hooks/auth/useAuthorizationInfo";
import { useCurrentTenant } from "@/common/hooks/entity/tenant/useCurrentTenant";
import { useAppHistory } from "@/common/hooks/routing/useAppHistory";
import useAppSnackbar from "@/common/hooks/useAppSnackbar";
import { useUserProfile } from "@/common/hooks/user/useUserProfile";
import { ApiEnumName } from "@/common/services/enum";
import { ViewVariant } from "@/common/ts/layout";
import { ValidationHelper } from "@/common/validation";
import { apiClient } from "@/core/api/ApiClient";
import {
  AppPermission,
  ApplyStatus,
  DamageCorrectionStatus,
  DamageCostEvaluationDto,
  DamageCostEvaluationStage,
  EntityOriginType,
  EntityType,
  GeneralDiscountInputDto,
  GeneralTaxInputDto,
  RepairSpecDataDto,
  RepairSpecDataInputDto,
} from "@/core/api/generated/v1";

import ContractLink from "../../../Contract/ContractLink";
import DamageDetectionLink from "../../../DamageDetection/DamageDetectionLink";
import EntityAffiliationInfoBlock from "../../../EntityAffiliation/EntityAffiliationInfoBlock";
import { GeneralBillToInfoDisplay } from "../../../General/BillTo/GeneralBillToInfoDisplay";
import { GeneralBillToInfoOfEntityUpdateModal } from "../../../General/BillTo/GeneralBillToInfoOfEntityUpdateModal";
import RepairSpecDataViewModal from "../../../RepairSpec/View/RepairSpecDataViewModal";
import RepairSpecViewModal from "../../../RepairSpec/View/RepairSpecViewModal";
import TenantInline from "../../../Tenant/TenantInline";
import VehicleLink from "../../../Vehicle/VehicleLink";
import { VisualModelViewerOfDamageCostEvaluation } from "../../../VehicleVisualModel/Viewer/v2/Entity/VisualModelViewerOfDamageCostEvaluation";
import CurrencyMismatchWithRepairSpecAlertCompact from "../../CurrencyMismatchWithRepairSpecAlertCompact";
import DamageCostEvaluationApplyDamageCorrectionsModal from "../../DamageCorrections/DamageCostEvaluationApplyDamageCorrectionsModal";
import DamageCostEvaluationDamageCorrectionsDisplayModal from "../../DamageCorrections/DamageCostEvaluationDamageCorrectionsDisplayModal";
import DamageCostEvaluationLink from "../../DamageCostEvaluationLink";

interface Props {
  damageCostEvaluation: DamageCostEvaluationDto;
  onUpdate: (newValue: DamageCostEvaluationDto | null) => void;
}

export default function OverviewTabContent({ damageCostEvaluation, onUpdate }: Props) {
  const { enqueueSnackbar } = useAppSnackbar();
  const history = useAppHistory();
  const { hasPermissions } = useAuthorizationInfo();
  const currentTenant = useCurrentTenant();
  const profile = useUserProfile();

  const [isSendToCustomer, setIsSendToCustomer] = useState(
    damageCostEvaluation.isSendToCustomer ?? false,
  );
  const [isViewDamageCorrectionsModalOpen, setIsViewDamageCorrectionsModalOpen] = useState(false);
  const [isApplyDamageCorrectionsModalOpen, setIsApplyDamageCorrectionsModalOpen] = useState(false);

  // menu, dialogs
  const [isAddToRepairOperationModalOpen, setIsAddToRepairOperationModalOpen] = useState(false);
  const [isViewRepairSpecModalOpen, setIsViewRepairSpecModalOpen] = useState(false);
  const [isViewRepairSpecDataModalOpen, setIsViewRepairSpecDataModalOpen] = useState(false);
  const [isCreateRepairSpecModalOpen, setIsCreateRepairSpecModalOpen] = useState(false);
  const [isConfirmCompletedModalOpen, setIsConfirmCompletedModalOpen] = useState(false);
  const [vehicleDamageIdsToAddToRepairOperation, setVehicleDamageIdsToAddToRepairOperation] =
    useState<
      | Array<{
          vehicleDamageId: string;
          cost?: number | null;
          discount?: GeneralDiscountInputDto;
          tax?: GeneralTaxInputDto;
        }>
      | undefined
    >(undefined);
  const [repairSpecIdToView, setRepairSpecIdToView] = useState<Nil<string>>(undefined);
  const [repairSpecDataToView, setRepairSpecDataToView] =
    useState<Nil<RepairSpecDataDto>>(undefined);
  const [createRepairSpecFromSpecData, setCreateRepairSpecFromSpecData] = useState<
    (RepairSpecDataDto & RepairSpecDataInputDto) | undefined
  >(undefined);

  return (
    <PageTabContent>
      <ViewLayout
        displayProps={{ viewVariant: ViewVariant.Tab }}
        header={
          <PageTabHeader
            title={"Details"}
            subtitle2={<EntityChangedByInfoCard entity={damageCostEvaluation} />}
          />
        }
      >
        <ViewContentBlock>
          {damageCostEvaluation?.stageHistory?.stage === DamageCostEvaluationStage.Draft && (
            <AuthorizedElement permissions={[AppPermission.DamageCostEvaluationManage]}>
              <Alert severity='info' icon={<AppIcon of='tip' />}>
                This operation is in{" "}
                <strong>
                  <InlineApiEnumValue
                    type='DamageCostEvaluationStage'
                    value={DamageCostEvaluationStage.Draft}
                  />
                </strong>{" "}
                stage. Please review and complete it in order for changes to take effect.
              </Alert>
            </AuthorizedElement>
          )}

          {/* <Stack direction={{ xxs: "column", md: "row" }} alignItems='flex-start' spacing={2}>
            <VehicleBlock
              sx={{ maxWidth: { md: "400px" } }}
              variant='reference'
              vehicle={damageCostEvaluation.vehicle}
              withDetailsToggle
              withLink
            />
            {damageCostEvaluation.contract && (
              <ContractBlock
                sx={{ maxWidth: { md: "400px" } }}
                variant='reference'
                contract={damageCostEvaluation.contract}
                withDetailsToggle
                withLink
              />
            )}
            {damageCostEvaluation.customer && (
              <CustomerBlock
                sx={{ maxWidth: { md: "400px" } }}
                variant='reference'
                customer={damageCostEvaluation.customer}
                withDetailsToggle
                withLink
              />
            )}
          </Stack> */}

          <Stack gap={1}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xxs: "1fr",
                  sm: "repeat(2, 1fr)",
                  md: "repeat(4, 1fr)",
                },
                gridTemplateRows: "auto",
                rowGap: 1,
                columnGap: 1,
              }}
            >
              {/* <InfoTile
                          label='Number'
                          iconOf='localNumber'
                          isEmpty={isNil(damageCostEvaluation?.localNumber)}
                        >
                          {damageCostEvaluation?.localNumber}
                        </InfoTile> */}

              <InfoTile
                label='Vehicle'
                iconOf='vehicle'
                isEmpty={isNil(damageCostEvaluation?.vehicle)}
              >
                <VehicleLink entity={damageCostEvaluation?.vehicle} />
              </InfoTile>

              <InfoTile
                label='Contract'
                iconOf='contract'
                isEmpty={isNil(damageCostEvaluation?.contract)}
              >
                <ContractLink entity={damageCostEvaluation?.contract} />
              </InfoTile>

              {renderIf()
                .if(!!damageCostEvaluation?.damageDetectionId)
                .then(
                  <InfoTile
                    label='Damage detection'
                    iconOf='damageDetection'
                    isEmpty={isNil(damageCostEvaluation?.damageDetectionId)}
                  >
                    <DamageDetectionLink
                      entityId={damageCostEvaluation.damageDetectionId}
                      entity={undefined}
                    />
                  </InfoTile>,
                )
                .elseif(!!damageCostEvaluation?.source)
                .then(
                  <InfoTile
                    label='Source'
                    iconOf='source'
                    isEmpty={isNil(damageCostEvaluation?.source)}
                  >
                    <GeneralEntitySourceLink source={damageCostEvaluation.source} />
                  </InfoTile>,
                )
                .render()}

              {damageCostEvaluation?.originalId && (
                <InfoTile
                  label={
                    <>
                      <AppTypography
                        component='span'
                        isInheritFontStyle
                        decoration={{ variant: "helpText" }}
                        tooltipProps={{
                          title: "Original damage cost evaluation from which this one is derived",
                        }}
                      >
                        Original
                      </AppTypography>{" "}
                      {damageCostEvaluation?.originalTenantId &&
                        damageCostEvaluation?.originalTenantId !==
                          damageCostEvaluation?.tenantId && (
                          <Chip
                            variant='outlined'
                            color='default'
                            size='small'
                            label={
                              <InlineApiEnumValue
                                type={ApiEnumName.EntityOriginType}
                                value={EntityOriginType.External}
                              />
                            }
                          />
                        )}
                    </>
                  }
                  iconOf='damageCostEvaluation'
                  isEmpty={isNil(damageCostEvaluation?.originalId)}
                >
                  <DamageCostEvaluationLink
                    entityId={damageCostEvaluation.originalId}
                    entity={undefined}
                  />
                </InfoTile>
              )}

              <InfoTile
                label='External number'
                iconOf='externalNumber'
                isEmpty={!damageCostEvaluation.externalNumber}
                endAdornment={
                  <BaseEditExternalNumberModal
                    initialValues={{ externalNumber: damageCostEvaluation.externalNumber }}
                    onConfirm={async (externalNumber) => {
                      const result =
                        await apiClient.damageCostEvaluationsApi.apiV1DamageCostEvaluationsDamageCostEvaluationIdExternalNumberPut(
                          {
                            nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
                            damageCostEvaluationId: damageCostEvaluation.id || "",
                            generalExternalNumberInputDto: { externalNumber },
                          },
                        );
                      onUpdate(result.data);
                    }}
                  />
                }
              >
                {damageCostEvaluation.externalNumber}
              </InfoTile>
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xxs: "1fr",
                  sm: "repeat(2, 1fr)",
                  md: "repeat(4, 1fr)",
                },
                gridTemplateRows: "auto",
                rowGap: 1,
                columnGap: 1,
              }}
            >
              <InfoTile
                label='Inspected at'
                iconOf='date'
                isEmpty={isNil(damageCostEvaluation.inspectedAt)}
              >
                <Datetime datetime={damageCostEvaluation.inspectedAt} />
              </InfoTile>

              <InfoTile
                label='Inspector'
                iconOf='inspector'
                isEmpty={isNil(damageCostEvaluation.inspector)}
              >
                <GeneralInspectorDisplay
                  inspector={damageCostEvaluation.inspector}
                  inlineUserProps={{ withLink: true, withAvatar: true, withPoper: true }}
                />
              </InfoTile>

              <InfoTile label='Currency' iconOf='currency' isEmpty={!damageCostEvaluation.currency}>
                <GeneralCurrencyDisplay currency={damageCostEvaluation.currency} />
              </InfoTile>

              <InfoTile
                label='Notes'
                iconOf='notes'
                isEmpty={!damageCostEvaluation.notes}
                endAdornment={
                  <BaseEditNotesModal
                    initialValues={{ notes: damageCostEvaluation.notes }}
                    onConfirm={async (notes) => {
                      const result =
                        await apiClient.damageCostEvaluationsApi.apiV1DamageCostEvaluationsDamageCostEvaluationIdNotesPut(
                          {
                            nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
                            damageCostEvaluationId: damageCostEvaluation.id || "",
                            generalNotesInputDto: { notes },
                          },
                        );
                      onUpdate(result.data);
                    }}
                  />
                }
              >
                {damageCostEvaluation.notes}
              </InfoTile>
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xxs: "1fr",
                  sm: "repeat(2, 1fr)",
                  md: "repeat(4, 1fr)",
                },
                gridTemplateRows: "auto",
                rowGap: 1,
                columnGap: 1,
              }}
            >
              <AuthorizedTenantElement tenantIds={[damageCostEvaluation.tenantId]}>
                <InfoTile
                  label='Bill to'
                  iconOf='data'
                  isEmpty={!damageCostEvaluation.billTo}
                  endAdornment={
                    <GeneralBillToInfoOfEntityUpdateModal
                      open={undefined}
                      onClose={undefined}
                      trigger={
                        <AppIconButton size='small'>
                          <AppIcon of='edit' fontSize='small' />
                        </AppIconButton>
                      }
                      updateProps={{
                        initialValue: damageCostEvaluation.billTo,
                        saveFunc: async (newValue) => {
                          const response =
                            await apiClient.damageCostEvaluationsApi.apiV1DamageCostEvaluationsDamageCostEvaluationIdBillToInfoPut(
                              {
                                nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
                                damageCostEvaluationId: damageCostEvaluation.id || "",
                                generalBillToInfoOfEntityUpdateDto: {
                                  ...newValue,
                                },
                              },
                            );
                          return response.data;
                        },
                        onSave: (newValue) => {
                          onUpdate(newValue);
                        },
                      }}
                    />
                  }
                >
                  <GeneralBillToInfoDisplay
                    value={damageCostEvaluation.billTo}
                    withDetailsPopover
                  />
                </InfoTile>
              </AuthorizedTenantElement>

              <InfoTile
                label='Damage corrections'
                iconOf='data'
                isEmpty={isNil(damageCostEvaluation.corrections?.correctionStatus)}
                endAdornment={
                  <Stack direction='row' gap={0.25}>
                    {damageCostEvaluation.corrections?.correctionStatus ===
                      DamageCorrectionStatus.Corrected && (
                      <DamageCostEvaluationDamageCorrectionsDisplayModal
                        trigger={
                          <AppIconButton
                            variant='text'
                            size='small'
                            color='default'
                            tooltipProps={{ title: "Quick view corrections" }}
                            onClick={() => {
                              setIsViewDamageCorrectionsModalOpen(true);
                            }}
                          >
                            <AppIcon of='quickView' />
                          </AppIconButton>
                        }
                        triggerParams={{
                          isDisableDefaultAction: true,
                        }}
                        open={isViewDamageCorrectionsModalOpen}
                        onClose={() => {
                          setIsViewDamageCorrectionsModalOpen(false);
                        }}
                        subProps={{
                          damageCostEvaluation,
                          corrections: damageCostEvaluation.corrections,
                        }}
                      />
                    )}

                    <AuthorizedTenantElement
                      tenantIds={[
                        damageCostEvaluation.tenantId,
                        damageCostEvaluation.vehicle?.tenantId,
                      ]}
                    >
                      {damageCostEvaluation.corrections?.canApply && (
                        <DamageCostEvaluationApplyDamageCorrectionsModal
                          trigger={
                            <AppIconButton
                              variant='text'
                              size='small'
                              color='default'
                              tooltipProps={{ title: "Apply corrections" }}
                              onClick={() => {
                                setIsViewDamageCorrectionsModalOpen(true);
                              }}
                            >
                              <AppIcon of='apply' />
                            </AppIconButton>
                          }
                          triggerParams={{
                            isDisableDefaultAction: true,
                          }}
                          open={isApplyDamageCorrectionsModalOpen}
                          onClose={() => {
                            setIsApplyDamageCorrectionsModalOpen(false);
                          }}
                          subProps={{
                            damageCostEvaluation,
                            onCancel: () => {
                              setIsApplyDamageCorrectionsModalOpen(false);
                            },
                            onSave: (newValue) => {
                              onUpdate(newValue);
                              setIsApplyDamageCorrectionsModalOpen(false);
                              setIsViewDamageCorrectionsModalOpen(true);
                            },
                          }}
                        />
                      )}
                    </AuthorizedTenantElement>
                  </Stack>
                }
              >
                <InlineApiEnumValue
                  type={ApiEnumName.DamageCorrectionStatus}
                  value={damageCostEvaluation.corrections?.correctionStatus}
                  withHelperTooltip
                />{" "}
                {damageCostEvaluation.corrections &&
                  damageCostEvaluation.corrections.correctionStatus ===
                    DamageCorrectionStatus.Corrected && (
                    <>
                      {damageCostEvaluation.corrections.correctedCount} of{" "}
                      {Math.max(
                        damageCostEvaluation.corrections.initialAndOriginalCount ?? 0,
                        damageCostEvaluation.corrections.correctedCount ?? 0,
                      )}{" "}
                      <Chip
                        sx={{ ml: 1 }}
                        variant={
                          damageCostEvaluation.corrections.applyStatus === ApplyStatus.Applied
                            ? "filled"
                            : "outlined"
                        }
                        size='small'
                        color={
                          damageCostEvaluation.corrections.applyStatus === ApplyStatus.Applied
                            ? "success"
                            : "default"
                        }
                        icon={
                          <AppIcon
                            of={
                              damageCostEvaluation.corrections.applyStatus === ApplyStatus.Applied
                                ? "check"
                                : "cross"
                            }
                          />
                        }
                        label={
                          <InlineApiEnumValue
                            type={ApiEnumName.ApplyStatus}
                            value={damageCostEvaluation.corrections.applyStatus}
                          />
                        }
                      />
                    </>
                  )}
              </InfoTile>
            </Box>
          </Stack>
        </ViewContentBlock>

        {/* Affiliation */}
        <EntityAffiliationInfoBlock
          entityType={EntityType.DamageCostEvaluation}
          entityId={damageCostEvaluation.id}
          entity={damageCostEvaluation}
          entityUpdatePermission={AppPermission.DamageCostEvaluationManage}
          infoDisplayProps={{
            withoutTenant: true,
          }}
          onUpdate={() => onUpdate(null)}
        />

        {/* Stages */}
        <ViewContentBlock>
          <Typography variant='h2'>Stages</Typography>

          <GeneralStageHistory
            stageEnumType='DamageCostEvaluationStage'
            stageHistory={damageCostEvaluation.stageHistory}
            withUpdateButtons={hasPermissions([AppPermission.DamageCostEvaluationManage])}
            onUpdateStage={async (newStage) => {
              try {
                const response =
                  await apiClient.damageCostEvaluationsApi.apiV1DamageCostEvaluationsDamageCostEvaluationIdStagePut(
                    {
                      nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
                      damageCostEvaluationId: damageCostEvaluation.id || "",
                      generalStageUpdateDtoOfDamageCostEvaluationStage: {
                        newStage: newStage,
                      },
                    },
                  );
                onUpdate && onUpdate(response.data);
              } catch (err) {
                const validation2 = ValidationHelper.handleApiErrorResponse(err);
                enqueueSnackbar(validation2.getErrorsAsString(), { variant: "error" });
              }
            }}
          />
        </ViewContentBlock>

        {/* Items */}
        <ViewContentBlock>
          <Typography variant='h2'>Items</Typography>

          <VisualModelViewerOfDamageCostEvaluation entity={damageCostEvaluation} />
        </ViewContentBlock>

        {damageCostEvaluation.attachments!.length !== 0 && (
          <ViewContentBlock>
            <FileListView
              files={FileItem.createManyFrom(damageCostEvaluation.attachments)}
              maxVisibleFiles={10}
              itemWidth={ATTACHMENTS_COMMON_ITEM_SIZES.WIDTH}
              itemHeight={ATTACHMENTS_COMMON_ITEM_SIZES.HEIGHT}
            />
          </ViewContentBlock>
        )}

        <ViewContentBlock>
          <DataTabular
            columns={[
              {
                field: "damages",
                title: "Damages",
                flex: 2,
                renderCell: (item) => (
                  <Stack spacing={0}>
                    {item.damages?.map((itemDamage, j) => (
                      <Stack key={j} direction='row' gap={0.5} alignItems='center'>
                        <VehicleDamageLink
                          vehicleId={itemDamage.damage?.vehicleId || ""}
                          entity={itemDamage.damage}
                          inlineProps={{ withIcon: true, withMenu: true }}
                        />
                        <Typography component='div' variant='body2' color='text.secondary'>
                          (<InlineApiEnumValue type='VehicleArea' value={itemDamage.damage?.area} />{" "}
                          /{" "}
                          {(itemDamage.damage?.projection && (
                            <InlineApiEnumValue
                              type='VehicleProjection'
                              value={itemDamage.damage?.projection}
                            />
                          )) ||
                            "-"}
                          )
                        </Typography>
                        {EntityHelper.isEntityDataInInlineMode(itemDamage.damage) && (
                          <Chip size='small' variant='outlined' label='Local' />
                        )}
                      </Stack>
                    ))}
                  </Stack>
                ),
              },
              {
                field: "name",
                title: "Name",
                flex: 1,
                renderCell: (item) => item.name,
              },
              {
                field: "repairSpec",
                title: "Repair spec",
                flex: 2,
                renderCell: (item) => {
                  const repairSpecPerms = DamageCostEvaluationHelper.getRepairSpecPermissions({
                    tenantId: currentTenant?.id,
                    profile,
                    damageCostEvaluation,
                    item,
                  });
                  if (!repairSpecPerms.canViewAny) {
                    return null;
                  }

                  const canQuickView = repairSpecPerms.canView || repairSpecPerms.canViewData;
                  const canCreateFromLocal =
                    repairSpecPerms.canSaveDataAsNewSpec && item.repairSpec;

                  return (
                    <Stack
                      direction='row'
                      gap={0.5}
                      sx={{ alignItems: "center", flexWrap: "wrap" }}
                    >
                      {renderIf()
                        .if(repairSpecPerms.canViewLink)
                        .then(
                          <RepairSpecLink
                            entity={item.repairSpec}
                            inlineProps={{ withIcon: true }}
                          />,
                        )
                        .elseif(repairSpecPerms.canViewName)
                        .then(item.repairSpec?.name && <Box>{item.repairSpec?.name}</Box>)
                        .else(<Box>{"-"}</Box>)
                        .render()}

                      {(canQuickView || canCreateFromLocal) && (
                        <MenuWithTrigger
                          withAuthCloseOnClick
                          trigger={
                            <AppIconButton size='small' sx={{ ml: 0.5, p: 0.25 }}>
                              <AppIcon of='moreVert' />
                            </AppIconButton>
                          }
                        >
                          {/* View repair spec data */}
                          {canQuickView && (
                            <MenuItem
                              dense
                              onClick={() => {
                                if (repairSpecPerms.canView) {
                                  setRepairSpecIdToView(item.repairSpec?.id);
                                  setIsViewRepairSpecModalOpen(true);
                                } else if (repairSpecPerms.canViewData) {
                                  setRepairSpecDataToView(item.repairSpec);
                                  setIsViewRepairSpecDataModalOpen(true);
                                }
                              }}
                            >
                              <AppIcon of='quickView' fontSize='small' sx={{ mr: 1 }} />
                              <Typography component='span'>Quick view</Typography>
                            </MenuItem>
                          )}

                          {/* Create repair spec from spec data */}
                          {canCreateFromLocal && (
                            <MenuItem
                              dense
                              onClick={() => {
                                setCreateRepairSpecFromSpecData(item.repairSpec);
                                setIsCreateRepairSpecModalOpen(true);
                              }}
                            >
                              <AppIcon of='create' fontSize='small' sx={{ mr: 1 }} />
                              <Typography component='span'>
                                Create new repair spec based on local data
                              </Typography>
                            </MenuItem>
                          )}
                        </MenuWithTrigger>
                      )}

                      {repairSpecPerms.canViewIsCreatedLocally && (
                        <Tooltip title='Repair spec was created locally in this damage cost evaluation without creating a dedicated spec.'>
                          <Chip size='small' variant='outlined' label='Created locally' />
                        </Tooltip>
                      )}

                      {repairSpecPerms.canViewIsEditedLocally && (
                        <Tooltip title='Repair spec was edited locally in this damage cost evaluation.'>
                          <Chip size='small' variant='outlined' label='Edited locally' />
                        </Tooltip>
                      )}

                      {repairSpecPerms.canViewIsExternal && (
                        <Tooltip
                          title={
                            <Box>
                              Repair spec was added from external company{" "}
                              <TenantInline
                                entityId={item.repairSpec?.tenantId}
                                entity={undefined}
                              />
                              .
                            </Box>
                          }
                        >
                          <Chip size='small' variant='outlined' label='External' />
                        </Tooltip>
                      )}
                    </Stack>
                  );
                },
              },
              {
                field: "attachments",
                title: "Attachments",
                width: 120,
                renderCell: (item) => (
                  <>
                    {!isEmpty(item.attachments) ? (
                      <FileListView
                        files={FileItem.createManyFrom(item.attachments)}
                        maxVisibleFiles={2}
                        itemWidth={ATTACHMENTS_COMMON_ITEM_SIZES.WIDTH}
                      />
                    ) : (
                      "-"
                    )}
                  </>
                ),
              },
              {
                field: "notes",
                title: "Notes",
                flex: 0,
                renderCell: (item) =>
                  item.notes ? (
                    <AppTooltip title={item.notes || ""}>
                      <AppTypography ellipsing={{ enabled: true }}>{item.notes}</AppTypography>
                    </AppTooltip>
                  ) : (
                    "-"
                  ),
              },
              {
                field: "total",
                title: "Sub total",
                flex: 0,
                renderCell: (item) => (
                  <AppPopover
                    hoverBehavior={{}}
                    trigger={
                      <AppTypography decoration={{ variant: "helpText" }}>
                        <CurrencyValue
                          value={item.subTotal}
                          currency={damageCostEvaluation.currency}
                        />
                      </AppTypography>
                    }
                  >
                    <AppPopoverContent>
                      <GeneralPriceSummaryDisplay
                        direction='column'
                        summary={{
                          currency: damageCostEvaluation.currency,
                          subTotal: item.subTotal,
                          discount: item.discount || undefined,
                          tax: item.tax || undefined,
                          total: item.total || 0,
                        }}
                      />
                    </AppPopoverContent>
                  </AppPopover>
                ),
              },
              {
                field: "alerts",
                title: "Alerts",
                flex: 0,
                renderCell: (item) => (
                  <>
                    {/* Currency mismatch alert */}
                    {damageCostEvaluation.currency && (
                      <CurrencyMismatchWithRepairSpecAlertCompact
                        entity={damageCostEvaluation}
                        item={item}
                      />
                    )}
                  </>
                ),
              },
            ]}
            rows={damageCostEvaluation.items!}
            getRowId={(item) => item.id!}
            borderVariant='bordered'
            getRowHeight={() => "auto"}
            rowHeight={65}
            isColumnSelectorEnabled={false}
            isColumnMenuEnabled={false}
            isPaginationEnabled={false}
            isWithScrollHelpers={false}
            renderRowAction={(actionParams) => {
              const vehicleDamageIds = actionParams.item.damages
                ?.map((x) => x.damage!)
                .filter((x) => x && x.id && EntityHelper.isEntityDataInRefMode(x))
                .map((x) => x.id!);
              const canAddToRepairOperation = TypeHelper.isNotEmptyArray(vehicleDamageIds);

              const vehicleDamageIdsCosts: Array<{
                vehicleDamageId: string;
                cost?: number | null;
                discount?: GeneralDiscountInputDto;
                tax?: GeneralTaxInputDto;
              }> =
                vehicleDamageIds?.map((damageId) => ({
                  vehicleDamageId: damageId,
                  cost: actionParams.item.subTotal || undefined,
                  discount: actionParams.item.discount || undefined,
                  tax: actionParams.item.tax || undefined,
                })) || [];

              return canAddToRepairOperation ? (
                <MenuWithTrigger
                  withAuthCloseOnClick
                  trigger={
                    <AppIconButton>
                      <AppIcon of='moreVert' />
                    </AppIconButton>
                  }
                >
                  {canAddToRepairOperation && (
                    <AppMenuItem
                      permissionsAny={[
                        AppPermission.RepairOperationPerform,
                        AppPermission.RepairOperationManage,
                      ]}
                      onClick={() => {
                        setVehicleDamageIdsToAddToRepairOperation(vehicleDamageIdsCosts);
                        setIsAddToRepairOperationModalOpen(true);
                      }}
                    >
                      <ListItemIcon>
                        <AppIcon of='add' fontSize='small' />
                      </ListItemIcon>
                      <ListItemText>Add damage to repair operation</ListItemText>
                    </AppMenuItem>
                  )}
                </MenuWithTrigger>
              ) : null;
            }}
          />
        </ViewContentBlock>

        {/* Summary */}
        {damageCostEvaluation.items!.length !== 0 && (
          <ViewContentBlock
            direction='row'
            justifyContent='flex-end'
            sx={{ alignSelf: "flex-end" }}
          >
            <GeneralPriceSummaryDisplay
              sx={{ minWidth: { xs: "100%", md: 300 } }}
              summary={{
                currency: damageCostEvaluation.currency,
                subTotal: damageCostEvaluation.subTotal,
                subTotalIncDiscount: damageCostEvaluation.subTotalIncDiscount,
                discount: damageCostEvaluation.discount,
                tax: damageCostEvaluation.tax,
                total: damageCostEvaluation.total,
              }}
            />
          </ViewContentBlock>
        )}

        {/* Complete helper buttons */}
        <AuthorizedTenantElement tenantIds={[damageCostEvaluation?.tenantId]}>
          <AuthorizedElement permissions={[AppPermission.DamageCostEvaluationManage]}>
            {damageCostEvaluation.stageHistory!.stage !== DamageCostEvaluationStage.Completed && (
              <Stack direction='row' spacing={1} sx={{ mt: 2 }} justifyContent='flex-end'>
                <Button
                  sx={{ minWidth: { md: 200 } }}
                  variant='outlined'
                  component={RouterLink}
                  to={ROUTE_PATH.DAMAGE_COST_EVALUATION_EDIT(damageCostEvaluation.id)}
                >
                  Edit
                </Button>

                <Button
                  sx={{ minWidth: { md: 600 } }}
                  variant='contained'
                  color='primary'
                  onClick={() => {
                    setIsConfirmCompletedModalOpen(true);
                  }}
                >
                  Complete
                </Button>

                {/* <AppSplitDropdownButton
                    sx={{ flex: 1 }}
                    isSelectAndTriggerAction={false}
                    buttonProps={{
                      variant: "contained",
                      color: "primary",
                      fullWidth: true,
                    }}
                    options={[
                      {
                        content: "Complete",
                        onClick: () => {
                          setIsSendToCustomer(false);
                          setIsConfirmCompletedModalOpen(true);
                        },
                      },
                      {
                        content: "Complete & send to customer",
                        if: !!damageCostEvaluation.contract,
                        onClick: () => {
                          setIsSendToCustomer(true);
                          setIsConfirmCompletedModalOpen(true);
                        },
                      },
                    ]}
                  /> */}
              </Stack>
            )}
          </AuthorizedElement>
        </AuthorizedTenantElement>

        {/* Confirm complete */}
        {damageCostEvaluation && (
          <ConfirmationModal
            title='Complete the cost evaluation?'
            body={
              <>
                <Box>
                  {`You're going to complete the damage cost evaluation`}{" "}
                  <strong>{damageCostEvaluation.localNumber}</strong>.
                </Box>

                <FormControl margin='dense' fullWidth>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isSendToCustomer}
                        onChange={(e) => setIsSendToCustomer(e.target.checked)}
                      />
                    }
                    label='Send to customer'
                  />
                </FormControl>
              </>
            }
            open={isConfirmCompletedModalOpen}
            onClose={() => setIsConfirmCompletedModalOpen(false)}
            onCancel={() => setIsConfirmCompletedModalOpen(false)}
            onConfirm={async () => {
              try {
                const response =
                  await apiClient.damageCostEvaluationsApi.apiV1DamageCostEvaluationsDamageCostEvaluationIdStageCompletePost(
                    {
                      nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
                      damageCostEvaluationId: damageCostEvaluation.id || "",
                      damageCostEvaluationCompleteDto: {
                        isSendToCustomer: isSendToCustomer,
                      },
                    },
                  );
                onUpdate && onUpdate(response.data);
                setIsConfirmCompletedModalOpen(false);
                history.goBack();
              } catch (err) {
                const validation2 = ValidationHelper.handleApiErrorResponse(err);
                enqueueSnackbar(validation2.getErrorsAsString(), { variant: "error" });
                throw err;
              }
            }}
          />
        )}

        {/* View repair spec */}
        {repairSpecIdToView && (
          <RepairSpecViewModal
            open={isViewRepairSpecModalOpen}
            onClose={() => {
              setIsViewRepairSpecModalOpen(false);
              setRepairSpecIdToView(undefined);
            }}
            viewProps={{
              repairSpecId: repairSpecIdToView,
              onDelete: null,
            }}
          />
        )}

        {/* View repair spec data */}
        {repairSpecDataToView && (
          <RepairSpecDataViewModal
            open={isViewRepairSpecDataModalOpen}
            onClose={() => {
              setIsViewRepairSpecDataModalOpen(false);
              setRepairSpecDataToView(undefined);
            }}
            viewProps={{
              repairSpec: repairSpecDataToView,
              onDelete: null,
            }}
          />
        )}

        {/* Create repair spec from local repair spec data */}
        {createRepairSpecFromSpecData && (
          <RepairSpecCreateUpdateModal
            open={isCreateRepairSpecModalOpen}
            onClose={() => {
              setIsCreateRepairSpecModalOpen(false);
              setCreateRepairSpecFromSpecData(undefined);
            }}
            createUpdateProps={{
              defaultValues: {
                data: createRepairSpecFromSpecData,
              },
              onSave: () => {
                setIsCreateRepairSpecModalOpen(false);
                setCreateRepairSpecFromSpecData(undefined);
              },
            }}
          />
        )}

        {/* Add to repair operation */}
        {damageCostEvaluation.vehicle?.id && vehicleDamageIdsToAddToRepairOperation && (
          <RepairOperationAddVehicleDamagesModal
            open={isAddToRepairOperationModalOpen}
            onClose={() => setIsAddToRepairOperationModalOpen(false)}
            vehicleId={damageCostEvaluation.vehicle.id}
            vehicleDamageIdsCosts={vehicleDamageIdsToAddToRepairOperation}
            vehicleDamageIds={undefined}
            currency={damageCostEvaluation.currency}
          />
        )}
      </ViewLayout>
    </PageTabContent>
  );
}
